import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Users, Shield, ShieldCheck, Eye, Trash2, Loader2, UserCog, Plus, Pencil, Crown,
} from "lucide-react";
import { toast } from "sonner";

export default function UsersPage() {
  const { api, user: currentUser, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [permissionSets, setPermissionSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "", email: "", password: "", role: "user", permission_set: "", company_id: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, permRes, compRes] = await Promise.all([
        isSuperAdmin ? api.get("/platform/users") : api.get("/company/users"),
        api.get("/settings/permission-sets").catch(() => ({ data: [] })),
        api.get("/companies").catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data);
      setPermissionSets(permRes.data);
      setCompanies(compRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const createUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("Name, email, and password are required");
      return;
    }

    setCreating(true);
    try {
      const payload = {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        company_id: newUser.company_id || (currentUser?.company_id || ""),
      };
      if (newUser.permission_set) payload.permission_set = newUser.permission_set;

      const endpoint = isSuperAdmin ? "/platform/users" : "/company/users";
      const res = await api.post(endpoint, payload);
      setUsers([...users, res.data]);
      setShowCreateDialog(false);
      setNewUser({ name: "", email: "", password: "", role: "user", permission_set: "", company_id: "" });
      toast.success("User created successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const updateUser = async () => {
    if (!editingUser) return;
    setUpdating(true);
    try {
      const updatePayload = {};
      if (editingUser.role) updatePayload.role = editingUser.role;
      if (isSuperAdmin && editingUser.name) updatePayload.name = editingUser.name;
      updatePayload.permission_set = editingUser.permission_set || "";
      if (isSuperAdmin) {
        updatePayload.company_id = (!editingUser.company_id || editingUser.company_id === "none") ? "" : editingUser.company_id;
      }
      const res = await api.put(`/users/${editingUser.id}`, updatePayload);
      setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, ...res.data } : u));
      setShowEditDialog(false);
      setEditingUser(null);
      toast.success("User updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setUpdating(false);
    }
  };

  const deleteUser = async () => {
    try {
      const endpoint = isSuperAdmin ? `/platform/users/${deleteId}` : `/users/${deleteId}`;
      await api.delete(endpoint);
      setUsers(users.filter(u => u.id !== deleteId));
      toast.success("User deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleteId(null);
    }
  };

  const getRoleBadge = (role) => {
    const cfg = {
      super_admin: { cls: "bg-amber-500/10 text-amber-600 border-amber-500/20", icon: Crown },
      admin: { cls: "bg-purple-500/10 text-purple-500 border-purple-500/20", icon: ShieldCheck },
      user: { cls: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20", icon: Eye },
    };
    const c = cfg[role] || cfg.user;
    const Icon = c.icon;
    return (
      <Badge className={`${c.cls} gap-1`} variant="outline">
        <Icon className="h-3 w-3" />
        {role?.replace("_", " ")}
      </Badge>
    );
  };

  const superAdminCount = users.filter(u => u.role === "super_admin").length;
  const adminCount = users.filter(u => u.role === "admin").length;
  const userCount = users.filter(u => u.role === "user" || !["super_admin", "admin"].includes(u.role)).length;

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <UserCog className="h-7 w-7 text-accent" />
            User Management
          </h1>
          <p className="text-muted-foreground text-sm">
            {isSuperAdmin ? "Manage all users across the platform" : "Manage users in your company"}
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-user-btn">
          <Plus className="mr-2 h-4 w-4" /> Create User
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1"><Users className="h-4 w-4" /><span className="text-sm">Total</span></div>
          <p className="text-2xl font-bold font-mono">{users.length}</p>
        </Card>
        {isSuperAdmin && (
          <Card className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1"><Crown className="h-4 w-4 text-amber-500" /><span className="text-sm">Super Admins</span></div>
            <p className="text-2xl font-bold font-mono text-amber-600">{superAdminCount}</p>
          </Card>
        )}
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1"><ShieldCheck className="h-4 w-4 text-purple-500" /><span className="text-sm">Admins</span></div>
          <p className="text-2xl font-bold font-mono text-purple-500">{adminCount}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1"><Eye className="h-4 w-4 text-emerald-500" /><span className="text-sm">Users</span></div>
          <p className="text-2xl font-bold font-mono text-emerald-500">{userCount}</p>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            {isSuperAdmin ? "Super Admin can create admins and users for any company" : "Create and manage users within your company"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  {isSuperAdmin && <TableHead>Company</TableHead>}
                  <TableHead>Permission Set</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Updated At</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">
                      {u.name}
                      {u.id === currentUser?.id && <Badge variant="outline" className="ml-2 text-xs">You</Badge>}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">{u.email}</TableCell>
                    <TableCell>{getRoleBadge(u.role)}</TableCell>
                    {isSuperAdmin && <TableCell className="text-sm text-muted-foreground">{u.company_name || "—"}</TableCell>}
                    <TableCell className="text-sm text-muted-foreground">{u.permission_set || "Default"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.updated_at ? new Date(u.updated_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => { setEditingUser({ ...u, permission_set: u.permission_set || "" }); setShowEditDialog(true); }}
                          disabled={u.role === "super_admin" && u.id !== currentUser?.id} data-testid={`edit-user-${u.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteId(u.id)}
                          disabled={u.id === currentUser?.id || u.role === "super_admin"} data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow><TableCell colSpan={isSuperAdmin ? 9 : 8} className="text-center py-12 text-muted-foreground">No users found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Role Info */}
      <Card>
        <CardHeader><CardTitle>Role Permissions</CardTitle></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {isSuperAdmin && (
              <div className="p-4 border rounded-lg border-amber-200 dark:border-amber-900">
                <div className="flex items-center gap-2 mb-2"><Crown className="h-5 w-5 text-amber-500" /><h4 className="font-semibold">Super Admin</h4></div>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>Full platform control</li><li>Manage all companies</li><li>Create admins & users</li><li>White-label branding</li>
                </ul>
              </div>
            )}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2"><ShieldCheck className="h-5 w-5 text-purple-500" /><h4 className="font-semibold">Admin</h4></div>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>Full access within company</li><li>Manage SSPs, DSPs, users</li><li>Settings & reports</li><li>Branding customization</li>
              </ul>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2"><Eye className="h-5 w-5 text-emerald-500" /><h4 className="font-semibold">User</h4></div>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>View dashboard & analytics</li><li>View SSPs, DSPs</li><li>View billing & reports</li><li>Read-only access</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>Are you sure? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteUser} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create New User</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} placeholder="Full name" data-testid="create-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="user@example.com" data-testid="create-user-email" />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} placeholder="Password" data-testid="create-user-password" />
            </div>
            {isSuperAdmin && (
              <div className="space-y-2">
                <Label>Company</Label>
                <Select value={newUser.company_id || "none"} onValueChange={v => setNewUser({ ...newUser, company_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="create-user-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No company</SelectItem>
                    {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select value={newUser.role} onValueChange={v => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Permission Set</Label>
              <Select value={newUser.permission_set || "none"} onValueChange={v => setNewUser({ ...newUser, permission_set: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="create-user-permission-set"><SelectValue placeholder="Default" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Default (Role-based)</SelectItem>
                  {permissionSets.map(ps => <SelectItem key={ps.id} value={ps.id}>{ps.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Assign granular permissions created in Settings</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createUser} disabled={creating} data-testid="submit-create-user">
              {creating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2"><Label>Name</Label><Input value={editingUser.name} onChange={e => setEditingUser({...editingUser, name: e.target.value})} disabled={!isSuperAdmin} className={!isSuperAdmin ? "bg-muted" : ""} data-testid="edit-user-name" /></div>
              <div className="space-y-2"><Label>Email</Label><Input value={editingUser.email} disabled className="bg-muted" /></div>
              {isSuperAdmin && (
              <div className="space-y-2">
                <Label>Company</Label>
                <Select value={editingUser.company_id || "none"} onValueChange={v => setEditingUser({ ...editingUser, company_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="edit-user-company"><SelectValue placeholder="No company" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No company</SelectItem>
                    {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              )}
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editingUser.role} onValueChange={v => setEditingUser({ ...editingUser, role: v })} disabled={editingUser.role === "super_admin"}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Permission Set</Label>
                <Select value={editingUser.permission_set || "none"} onValueChange={v => setEditingUser({ ...editingUser, permission_set: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="edit-user-permission-set"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Default (Role-based)</SelectItem>
                    {permissionSets.map(ps => <SelectItem key={ps.id} value={ps.id}>{ps.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={updateUser} disabled={updating} data-testid="submit-edit-user">
              {updating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
